import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CrisisAlertRoundedIcon from "@mui/icons-material/CrisisAlertRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import MessageIcon from "@mui/icons-material/Message";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import NotificationAddIcon from "@mui/icons-material/NotificationAdd";
import RepeatRoundedIcon from "@mui/icons-material/RepeatRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import {
    Box,
    ButtonBase,
    IconButton,
    Skeleton,
    Typography,
    useMediaQuery,
} from "@mui/material";
import EddyIconSvg from "SvgIcons/EddyIconSvg";
import { SparklingGradientMultiStar } from "SvgIcons/SparklingGradientStar";
import theme from "Theme/AppTheme";
import SearchSubscriptionDialog from "components/Conversations/Views/Drawer/components/Subscriptions/SearchSubscriptionDialog";
import { areFiltersEqual } from "components/Conversations/Views/Drawer/components/Subscriptions/helpers";
import { AgentLogo } from "components/KBo/Branding/AgentLogo";
import AgentStore from "components/KBo/Stores/AgentStore";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import AcxConfirmationDialog from "components/UI/AcxConfirmationDialog";
import AcxHierarchySelector from "components/UI/AcxHierarchySelector/AcxHierarchySelector";
import AcxMenu, { StyledMenuLabel } from "components/UI/Menu/AcxMenu";
import Markdown from "markdown-to-jsx";
import { observer } from "mobx-react";
import moment from "moment";
import React from "react";
import { useNavigate } from "react-router";
import {
    buildProactiveInsightKboFilterContext,
    buildProactiveInsightKboPrompt,
    buildProactiveInsightSubscriptionPayload,
    buildProactiveInsightViewInConversationsPayload,
    insightTypeToTitle,
    normalizeProactiveInsightType,
    proactiveInsightIconTypes,
    proactiveInsightNeedsLlmClassifiers,
} from "services/ProactiveInsightsService";
import { ApplicationFilterItemsStore } from "stores/ApplicationFilters/ApplicationFilterItemsStore";
import { ApplicationFilterValuesStore } from "stores/ApplicationFilters/ApplicationFilterValuesStore";
import {
    ApplicationFiltersStore,
    SavedFilter,
} from "stores/ApplicationFilters/ApplicationFiltersStore";
import { AuthStore } from "stores/AuthStore";
import ContextStore from "stores/ContextStore";
import OrgOptionsStore from "stores/OrgOptionsStore";
import { stringPluralizer } from "utils/StringPluralizer";
import { useStore } from "utils/useStore";
import SignalsPageStore from "../Store/SignalsPageStore";
import type { HomeInsight } from "./HomeInsights.models";

type Props = {
    insights: HomeInsight[];
    isLoading?: boolean;
};

const conversationsRoute = "/app/conversations?fromDomain=signals";

const formatInsightRangeDate = (date: moment.Moment) => date.format("MMM D");

const getInsightComparisonDateRanges = () => {
    const currentWeekStart = moment().startOf("isoWeek");
    const lastWeekStart = currentWeekStart.clone().subtract(1, "week");
    const lastWeekEnd = currentWeekStart.clone().subtract(1, "day");
    const weekPriorStart = lastWeekStart.clone().subtract(1, "week");
    const weekPriorEnd = lastWeekStart.clone().subtract(1, "day");

    return {
        lastWeek: `${formatInsightRangeDate(
            lastWeekStart,
        )} - ${formatInsightRangeDate(lastWeekEnd)}`,
        weekPrior: `${formatInsightRangeDate(
            weekPriorStart,
        )} - ${formatInsightRangeDate(weekPriorEnd)}`,
    };
};

const appendConversationCountToSummary = (
    summary: string,
    count?: number | null,
) => {
    if (typeof count !== "number") {
        return summary;
    }

    const countText = `(${count.toLocaleString()} ${stringPluralizer(
        "conversation",
        count,
        "s",
    )})`;
    const trimmedSummary = summary.trimEnd();
    const trailingPunctuation = trimmedSummary.match(/[.!?]+$/)?.[0];

    if (trailingPunctuation) {
        return `${trimmedSummary.slice(
            0,
            -trailingPunctuation.length,
        )} ${countText}${trailingPunctuation}`;
    }

    return `${trimmedSummary} ${countText}`;
};

const HomeInsightsCards: React.FC<Props> = ({
    insights,
    isLoading = false,
}) => {
    const navigate = useNavigate();
    const applicationFiltersStore = useStore(ApplicationFiltersStore);
    const applicationFilterValuesStore = useStore(ApplicationFilterValuesStore);
    const applicationFilterItemsStore = useStore(ApplicationFilterItemsStore);
    const contextStore = useStore(ContextStore);
    const messageStore = useStore(MessageStore);
    const authStore = useStore(AuthStore);
    const agentStore = useStore(AgentStore);
    const canViewKbo = authStore.canUserView("View KBo");
    const orgOptionsStore = useStore(OrgOptionsStore);
    const signalsPageStore = useStore(SignalsPageStore);
    // The sanitizer drops Question Result rows this viewer cannot see, so
    // subscription matching must recompute once the module catalog resolves.
    const questionResultModuleAccessKey =
        applicationFilterValuesStore.questionResultModuleAccessKey;

    const [isExpanded, setIsExpanded] = React.useState(true);
    const [headerMenuAnchorEl, setHeaderMenuAnchorEl] =
        React.useState<HTMLElement | null>(null);
    const [isCustomizeOpen, setIsCustomizeOpen] = React.useState(false);
    const [isSavingCustomize, setIsSavingCustomize] = React.useState(false);
    const [selectedHierarchyIds, setSelectedHierarchyIds] = React.useState<
        string[]
    >([]);
    const [page, setPage] = React.useState(0);
    const [menuAnchorEl, setMenuAnchorEl] = React.useState<HTMLElement | null>(
        null,
    );
    const [activeInsightId, setActiveInsightId] = React.useState<string | null>(
        null,
    );
    const [subscriptionDialogState, setSubscriptionDialogState] =
        React.useState<{
            filters: ReturnType<typeof applicationFiltersStore.toFilterObject>;
            selectedSavedFilter?: SavedFilter;
            initialKboAnalysisPrompt?: string;
            kboAnalysisPromptPlaceholder?: string;
        } | null>(null);

    const insightDateRanges = React.useMemo(
        () => getInsightComparisonDateRanges(),
        [],
    );

    const isXlUp = useMediaQuery(theme.breakpoints.up("xl"));
    const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));
    const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
    const maxInsights = isXlUp ? 4 : isLgUp ? 3 : isMdUp ? 2 : 1;
    const totalInsights = insights.length;
    const hasInsights = totalInsights > 0;
    const showSkeleton = isLoading && !hasInsights;
    const totalPages = Math.ceil(totalInsights / maxInsights);
    const clampedPage = Math.min(page, Math.max(totalPages - 1, 0));
    const cardWidth = `calc((100% - ${theme.spacing(
        2.5 * (maxInsights - 1),
    )}) / ${maxInsights})`;
    // On the last page, shift the window backward to always show a full set of cards
    const isLastPage = totalPages > 1 && clampedPage === totalPages - 1;
    const pageStartIndex = isLastPage
        ? Math.max(totalInsights - maxInsights, 0)
        : clampedPage * maxInsights;
    const visibleInsights = React.useMemo(
        () => insights.slice(pageStartIndex, pageStartIndex + maxInsights),
        [insights, maxInsights, pageStartIndex],
    );
    const activeInsight = React.useMemo(
        () =>
            insights.find((insight) => insight.id === activeInsightId) ?? null,
        [activeInsightId, insights],
    );
    const isZeroConversations = (activeInsight?.delta ?? 0) <= -100;
    const viewInConversationsDisabledTooltip =
        "No conversations to view (100% decrease).";

    React.useEffect(() => {
        if (page !== clampedPage) {
            setPage(clampedPage);
        }
    }, [page, clampedPage]);

    const closeMenu = React.useCallback(() => {
        setMenuAnchorEl(null);
        setActiveInsightId(null);
    }, []);

    const openMenu = React.useCallback(
        (event: React.MouseEvent<HTMLElement>, insightId: string) => {
            setMenuAnchorEl(event.currentTarget);
            setActiveInsightId(insightId);
        },
        [],
    );

    const openHeaderMenu = React.useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
            setHeaderMenuAnchorEl(event.currentTarget);
        },
        [],
    );

    const closeHeaderMenu = React.useCallback(() => {
        setHeaderMenuAnchorEl(null);
    }, []);

    const openCustomize = React.useCallback(() => {
        setSelectedHierarchyIds([
            ...signalsPageStore.homeInsightHierarchyIds,
        ]);
        setIsCustomizeOpen(true);
        closeHeaderMenu();
    }, [signalsPageStore.homeInsightHierarchyIds, closeHeaderMenu]);

    const closeCustomize = React.useCallback(() => {
        setIsCustomizeOpen(false);
    }, []);

    const handleSaveCustomize = React.useCallback(async () => {
        setIsSavingCustomize(true);
        try {
            await signalsPageStore.saveHomeInsightHierarchyIds(
                selectedHierarchyIds,
            );
            setIsCustomizeOpen(false);
        } finally {
            setIsSavingCustomize(false);
        }
    }, [signalsPageStore, selectedHierarchyIds]);

    const toggleExpanded = React.useCallback(() => {
        setIsExpanded((prev) => !prev);
        closeHeaderMenu();
    }, [closeHeaderMenu]);

    const handleAskKbo = React.useCallback(async () => {
        if (!activeInsight?.appliedFilters) {
            closeMenu();
            messageStore.logError(
                "This insight does not include filters for KBo yet.",
            );
            return;
        }

        const needsModelResults = proactiveInsightNeedsLlmClassifiers(
            activeInsight.insightType,
        );

        if (needsModelResults && !orgOptionsStore.llmClassifiers?.length) {
            await orgOptionsStore.refreshLLMClassifiers();
        }

        const filterContext = buildProactiveInsightKboFilterContext(
            activeInsight.insightType,
            activeInsight.appliedFilters,
            insightTypeToTitle[activeInsight.insightType],
            orgOptionsStore.llmClassifiers,
        );
        const analysisSubject = buildProactiveInsightKboPrompt(
            activeInsight.insightType,
            activeInsight.appliedFilters,
        );

        if (!filterContext || !analysisSubject) {
            closeMenu();
            messageStore.logError(
                "This insight could not be translated into a KBo request.",
            );
            return;
        }

        const insight = activeInsight;
        agentStore.currentChatStore.handleSuggestion({
            content: insightTypeToTitle[insight.insightType],
            action: () =>
                agentStore.currentChatStore.analyzeProactiveInsight(
                    insight.insightType,
                    filterContext,
                    analysisSubject,
                ),
            newChatName: `Analyze ${insightTypeToTitle[insight.insightType]}`,
        });
        agentStore.openDrawer();
        closeMenu();
    }, [activeInsight, agentStore, orgOptionsStore, closeMenu, messageStore]);

    const handleViewInConversations = React.useCallback(async () => {
        if (!activeInsight?.appliedFilters) {
            closeMenu();
            messageStore.logError(
                "This insight does not include filters for Conversations yet.",
            );
            return;
        }

        const needsModelResults = proactiveInsightNeedsLlmClassifiers(
            activeInsight.insightType,
        );

        if (
            needsModelResults &&
            !orgOptionsStore.llmClassifiersLoading &&
            !orgOptionsStore.llmClassifiers.length
        ) {
            await orgOptionsStore.refreshLLMClassifiers();
        }

        const viewInConversationsPayload =
            buildProactiveInsightViewInConversationsPayload(
                activeInsight.insightType,
                activeInsight.appliedFilters,
                orgOptionsStore.llmClassifiers,
            );

        if (!viewInConversationsPayload) {
            closeMenu();
            messageStore.logError(
                "This insight could not be translated into Conversations filters.",
            );
            return;
        }

        applicationFiltersStore.saveCurrentFilterState("before-drilldown");
        applicationFiltersStore.saveExternalFilterState(
            "after-drilldown",
            viewInConversationsPayload.filters,
        );
        applicationFiltersStore.currentlyAppliedFilters =
            viewInConversationsPayload.filters;

        contextStore.setupContext({
            actionTaken: "ViewInConversations",
            filters: viewInConversationsPayload.smartBannerFilters,
            hierarchyName: viewInConversationsPayload.hierarchyName,
            proactiveInsightType: activeInsight.insightType,
            selectedSavedFilter: undefined,
            source: "proactiveInsights",
        });

        closeMenu();
        navigate(conversationsRoute);
    }, [
        activeInsight,
        orgOptionsStore,
        applicationFiltersStore,
        closeMenu,
        contextStore,
        messageStore,
        navigate,
    ]);

    const findMatchingSavedFilter = React.useCallback(
        (
            filters: ReturnType<typeof applicationFiltersStore.toFilterObject>,
        ) => {
            // Read so exhaustive-deps accepts the key below: the real dependency
            // runs through mobx state inside stripInaccessibleFilters, where the
            // linter cannot see it.
            void questionResultModuleAccessKey;

            const comparableFilters =
                applicationFilterItemsStore.stripInaccessibleFilters(
                    filters,
                ).filters;

            const matches = applicationFiltersStore.savedFilters.filter(
                (savedFilter) =>
                    areFiltersEqual(
                        applicationFilterItemsStore.stripInaccessibleFilters(
                            savedFilter.value,
                        ).filters,
                        comparableFilters,
                    ),
            );

            return (
                matches.find(
                    (savedFilter) =>
                        !!savedFilter.applicationFilterSubscriptionId,
                ) ?? matches[0]
            );
        },
        [
            applicationFilterItemsStore,
            applicationFiltersStore,
            questionResultModuleAccessKey,
        ],
    );

    const activeInsightSubscriptionMatch = React.useMemo(() => {
        if (!activeInsight?.appliedFilters) {
            return undefined;
        }

        const payload = buildProactiveInsightSubscriptionPayload(
            activeInsight.insightType,
            activeInsight.appliedFilters,
            orgOptionsStore.llmClassifiers,
        );

        if (!payload) {
            return undefined;
        }

        return findMatchingSavedFilter(payload.filters);
    }, [
        activeInsight,
        orgOptionsStore.llmClassifiers,
        findMatchingSavedFilter,
    ]);

    const handleSubscribe = React.useCallback(async () => {
        if (!activeInsight?.appliedFilters) {
            closeMenu();
            messageStore.logError(
                "This insight does not include filters for subscriptions yet.",
            );
            return;
        }

        if (
            proactiveInsightNeedsLlmClassifiers(activeInsight.insightType) &&
            !orgOptionsStore.llmClassifiers?.length
        ) {
            await orgOptionsStore.refreshLLMClassifiers();
        }

        const subscriptionPayload = buildProactiveInsightSubscriptionPayload(
            activeInsight.insightType,
            activeInsight.appliedFilters,
            orgOptionsStore.llmClassifiers,
        );

        if (!subscriptionPayload) {
            closeMenu();
            messageStore.logError(
                "This insight could not be translated into subscription filters.",
            );
            return;
        }

        const selectedSavedFilter = findMatchingSavedFilter(
            subscriptionPayload.filters,
        );

        closeMenu();
        setSubscriptionDialogState({
            filters: subscriptionPayload.filters,
            selectedSavedFilter,
            initialKboAnalysisPrompt: subscriptionPayload.kboAnalysisPrompt,
            kboAnalysisPromptPlaceholder:
                buildProactiveInsightKboPrompt(
                    activeInsight.insightType,
                    activeInsight.appliedFilters,
                ) ?? "",
        });
    }, [
        activeInsight,
        orgOptionsStore,
        closeMenu,
        findMatchingSavedFilter,
        messageStore,
    ]);

    const getInsightIcon = (insightId: string) => {
        const normalizedInsightId = normalizeProactiveInsightType(insightId);

        if (proactiveInsightIconTypes.resolution.has(normalizedInsightId)) {
            return (
                <TrendingUpRoundedIcon
                    sx={{ fontSize: 12, color: theme.palette.white.main }}
                />
            );
        }
        if (proactiveInsightIconTypes.risk.has(normalizedInsightId)) {
            return (
                <CrisisAlertRoundedIcon
                    sx={{ fontSize: 12, color: theme.palette.white.main }}
                />
            );
        }
        if (proactiveInsightIconTypes.recurring.has(normalizedInsightId)) {
            return (
                <RepeatRoundedIcon
                    sx={{ fontSize: 12, color: theme.palette.white.main }}
                />
            );
        }
        if (proactiveInsightIconTypes.eddy.has(normalizedInsightId)) {
            return (
                <EddyIconSvg
                    style={{
                        width: 12,
                        height: 12,
                        filter: "brightness(0) invert(1)",
                    }}
                />
            );
        }

        return (
            <EddyIconSvg
                style={{
                    width: 12,
                    height: 12,
                    filter: "brightness(0) invert(1)",
                }}
            />
        );
    };

    const getTitleBackground = (insightId: string) => {
        const normalizedInsightId = normalizeProactiveInsightType(insightId);

        if (proactiveInsightIconTypes.resolution.has(normalizedInsightId)) {
            return "linear-gradient(93deg, #17A4EB -25.8%, #3664D6 117.82%)";
        }
        if (proactiveInsightIconTypes.risk.has(normalizedInsightId)) {
            return "linear-gradient(91deg, #30CAAF 1.22%, #239C98 110.87%)";
        }
        if (proactiveInsightIconTypes.recurring.has(normalizedInsightId)) {
            return "linear-gradient(93deg, #229995 -25.8%, #115452 117.82%)";
        }
        return "linear-gradient(90deg, #1E40AF 0%, #3664D6 100%)";
    };

    const navButtonSx = {
        backgroundColor: theme.palette.gray[100],
        color: theme.palette.black.main,
        border: `1px solid ${theme.palette.gray[200]}`,
        borderRadius: 2,
        padding: 0,
        flexShrink: 0,
        "&:hover": {
            backgroundColor: theme.palette.gray[100],
        },
    };

    return (
        <Box
            sx={{
                width: "100%",
                background:
                    "linear-gradient(92deg, #0C133A 1.01%, #1D2C69 102.56%)",
                px: 0,
                py: 2.5,
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    px: 3,
                }}
            >
                <Box sx={{ minWidth: 0 }}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            flexWrap: "wrap",
                        }}
                    >
                        <Typography
                            sx={{
                                color: theme.palette.white.main,
                                fontSize: "18px",
                                fontWeight: 600,
                            }}
                        >
                            What&apos;s Changed
                        </Typography>
                        <SparklingGradientMultiStar />
                    </Box>
                    <Typography
                        sx={{
                            color: theme.palette.gray[300],
                            fontSize: "14px",
                            mt: 0.5,
                            fontWeight: 400,
                        }}
                    >
                        {`Each insight refreshes every Monday and reflects a meaningful change in your data — comparing last week (${insightDateRanges.lastWeek}) to the week prior (${insightDateRanges.weekPrior}).`}
                    </Typography>
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        flexShrink: 0,
                    }}
                >
                    <IconButton
                        aria-label="Insights options"
                        data-pendo-id="proactive-insights-options"
                        onClick={openHeaderMenu}
                        sx={{ color: theme.palette.gray[200] }}
                    >
                        <MoreVertIcon />
                    </IconButton>
                </Box>
            </Box>
            {isExpanded && (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mt: 2,
                        px: 3,
                    }}
                >
                    {!showSkeleton && totalPages > 1 && (
                        <IconButton
                            aria-label="Previous insights"
                            onClick={() =>
                                setPage((prev) => Math.max(prev - 1, 0))
                            }
                            sx={{
                                ...navButtonSx,
                                visibility:
                                    clampedPage > 0 ? "visible" : "hidden",
                            }}
                            size="small"
                        >
                            <ChevronLeftIcon />
                        </IconButton>
                    )}
                    <Box
                        sx={{
                            display: "flex",
                            flex: "1 1 0",
                            minWidth: 0,
                            gap: 2.5,
                            justifyContent: "flex-start",
                        }}
                    >
                        {showSkeleton ? (
                            Array.from({ length: maxInsights }, (_, index) => (
                                <Box
                                    key={`home-insight-skeleton-${index}`}
                                    sx={{
                                        flex: `0 0 ${cardWidth}`,
                                        maxWidth: cardWidth,
                                        minWidth: 0,
                                        overflow: "hidden",
                                        minHeight: 140,
                                        backgroundColor:
                                            "rgba(255,255,255,0.9)",
                                        borderRadius: 1.5,
                                        padding: 2,
                                        boxShadow:
                                            "0px 1px 8px 0px rgba(0,0,0,0.05)",
                                        display: "flex",
                                        flexDirection: "column",
                                    }}
                                >
                                    <Skeleton
                                        variant="rounded"
                                        height={26}
                                        width="60%"
                                        sx={{
                                            backgroundColor:
                                                theme.palette.gray[300],
                                            borderRadius: "999px",
                                        }}
                                    />
                                    <Skeleton
                                        variant="text"
                                        height={18}
                                        sx={{
                                            mt: 1,
                                            backgroundColor:
                                                theme.palette.gray[300],
                                        }}
                                    />
                                    <Skeleton
                                        variant="text"
                                        height={18}
                                        sx={{
                                            backgroundColor:
                                                theme.palette.gray[300],
                                        }}
                                    />
                                </Box>
                            ))
                        ) : hasInsights ? (
                            visibleInsights.map((insight, index) => (
                                <Box
                                    key={`${insight.id}-${pageStartIndex}-${index}`}
                                    sx={{
                                        flex: `0 0 ${cardWidth}`,
                                        maxWidth: cardWidth,
                                        minWidth: 0,
                                        overflow: "hidden",
                                        backgroundColor:
                                            "rgba(255,255,255,0.9)",
                                        borderRadius: 1.5,
                                        padding: 2,
                                        boxShadow:
                                            "0px 1px 8px 0px rgba(0,0,0,0.05)",
                                        display: "flex",
                                        flexDirection: "column",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "inline-flex",
                                            alignSelf: "flex-start",
                                            alignItems: "center",
                                            gap: 0.75,
                                            background: getTitleBackground(
                                                insight.insightType,
                                            ),
                                            borderRadius: "999px",
                                            px: 1.25,
                                            py: 0.5,
                                            mt: 0.5,
                                        }}
                                    >
                                        {getInsightIcon(insight.insightType)}
                                        <Typography
                                            sx={{
                                                color: theme.palette.white.main,
                                                fontSize: "13px",
                                                fontWeight: 700,
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {insight.label}
                                        </Typography>
                                    </Box>
                                    <Typography
                                        component="div"
                                        sx={{
                                            color: theme.palette.gray[700],
                                            fontSize: "14px",
                                            lineHeight: "20px",
                                            mt: 1,
                                            "& p": {
                                                margin: 0,
                                            },
                                            "& p + p": {
                                                mt: 0.75,
                                            },
                                            "& ul, & ol": {
                                                margin: theme.spacing(
                                                    0.75,
                                                    0,
                                                    0,
                                                ),
                                                paddingLeft: theme.spacing(2.5),
                                            },
                                            "& li + li": {
                                                mt: 0.25,
                                            },
                                            "& a": {
                                                color: theme.palette.primary
                                                    .main,
                                            },
                                            "& code": {
                                                fontFamily: "monospace",
                                                fontSize: "0.95em",
                                            },
                                        }}
                                    >
                                        <Markdown>
                                            {appendConversationCountToSummary(
                                                insight.summary,
                                                insight.currentMatchingConversationCount,
                                            )}
                                        </Markdown>
                                    </Typography>
                                    <Box sx={{ mt: "auto", pt: 2 }}>
                                        <ButtonBase
                                            data-pendo-id="proactive-insights-take-action"
                                            onClick={(event) =>
                                                openMenu(event, insight.id)
                                            }
                                            sx={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                gap: 0.5,
                                                color: theme.palette.primary
                                                    .main,
                                                fontSize: "14px",
                                                fontWeight: 700,
                                                borderRadius: 1,
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontSize: "14px",
                                                    fontWeight: 700,
                                                    color: theme.palette.primary
                                                        .main,
                                                }}
                                            >
                                                Take Action
                                            </Typography>
                                            <KeyboardArrowDownRoundedIcon />
                                        </ButtonBase>
                                    </Box>
                                </Box>
                            ))
                        ) : (
                            <Box
                                sx={{
                                    flex: "1 1 100%",
                                    minHeight: 100,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    px: 2,
                                }}
                            >
                                <Typography
                                    sx={{
                                        color: theme.palette.white.main,
                                        fontSize: "16px",
                                        lineHeight: "22px",
                                        fontWeight: 600,
                                        textAlign: "center",
                                    }}
                                >
                                    There are no meaningful changes this week.
                                    Check back next week!
                                </Typography>
                            </Box>
                        )}
                    </Box>
                    {!showSkeleton && totalPages > 1 && (
                        <IconButton
                            aria-label="Next insights"
                            onClick={() =>
                                setPage((prev) =>
                                    Math.min(prev + 1, totalPages - 1),
                                )
                            }
                            sx={{
                                ...navButtonSx,
                                visibility:
                                    clampedPage < totalPages - 1
                                        ? "visible"
                                        : "hidden",
                            }}
                            size="small"
                        >
                            <ChevronRightIcon />
                        </IconButton>
                    )}
                </Box>
            )}
            <AcxMenu
                anchorElement={menuAnchorEl}
                onMenuClose={closeMenu}
                menuItems={[
                    {
                        id: "proactive-insights-view-in-conversations",
                        label: (
                            <StyledMenuLabel>
                                View in Conversations
                            </StyledMenuLabel>
                        ),
                        props: {
                            onClick: handleViewInConversations,
                            className: "proactive-insights-view-in-convos",
                            disabled: isZeroConversations,
                        },
                        icon: <MessageIcon />,
                        toolTipText: isZeroConversations
                            ? viewInConversationsDisabledTooltip
                            : undefined,
                    },
                    ...(canViewKbo
                        ? [
                              {
                                  id: "proactive-insights-ask-kbo",
                                  label: (
                                      <StyledMenuLabel>Ask KBo</StyledMenuLabel>
                                  ),
                                  props: {
                                      onClick: handleAskKbo,
                                      disabled: isZeroConversations,
                                  },
                                  icon: (
                                      <AgentLogo
                                          gradient
                                          height="20px"
                                          width="20px"
                                      />
                                  ),
                                  toolTipText: isZeroConversations
                                      ? viewInConversationsDisabledTooltip
                                      : undefined,
                              },
                          ]
                        : []),
                    {
                        id: "proactive-insights-subscribe",
                        label: (
                            <StyledMenuLabel>
                                {activeInsightSubscriptionMatch?.applicationFilterSubscriptionId
                                    ? "Edit Subscription"
                                    : "Subscribe"}
                            </StyledMenuLabel>
                        ),
                        props: {
                            onClick: handleSubscribe,
                        },
                        icon: <NotificationAddIcon />,
                    },
                ]}
            />
            {subscriptionDialogState && (
                <SearchSubscriptionDialog
                    isOpen={!!subscriptionDialogState}
                    onClose={() => setSubscriptionDialogState(null)}
                    source="proactive-insights"
                    applicationFiltersStore={applicationFiltersStore}
                    applicationFilterValuesStore={applicationFilterValuesStore}
                    currentlyAppliedFilters={subscriptionDialogState.filters}
                    messageStore={messageStore}
                    selectedSavedFilter={
                        subscriptionDialogState.selectedSavedFilter
                    }
                    initialKboAnalysisPrompt={
                        subscriptionDialogState.initialKboAnalysisPrompt
                    }
                    kboAnalysisPromptPlaceholder={
                        subscriptionDialogState.kboAnalysisPromptPlaceholder
                    }
                    subscribeTitle="Subscribe to Insight"
                    editTitle="Edit Insight Subscription"
                    filtersHeading="You're tracking:"
                    dataTestId="subscribe-to-insight-modal"
                />
            )}
            <AcxMenu
                anchorElement={headerMenuAnchorEl}
                onMenuClose={closeHeaderMenu}
                menuItems={[
                    {
                        id: "proactive-insights-customize",
                        label: <StyledMenuLabel>Customize</StyledMenuLabel>,
                        props: {
                            onClick: openCustomize,
                        },
                        icon: <SettingsRoundedIcon />,
                    },
                    {
                        id: "proactive-insights-collapse-expand",
                        label: (
                            <StyledMenuLabel>
                                {isExpanded ? "Collapse" : "Expand"}
                            </StyledMenuLabel>
                        ),
                        props: {
                            onClick: toggleExpanded,
                        },
                        icon: isExpanded ? (
                            <ExpandLessRoundedIcon />
                        ) : (
                            <ExpandMoreRoundedIcon />
                        ),
                    },
                ]}
            />
            {isCustomizeOpen && (
                <AcxConfirmationDialog
                    isOpen={isCustomizeOpen}
                    title="Customize your insights"
                    subTitle="Manage what you see. Changes apply to your view only."
                    maxWidth="sm"
                    fullWidth
                    confirmButtonText="Save"
                    cancelButtonText="Cancel"
                    isLoading={isSavingCustomize}
                    onConfirm={handleSaveCustomize}
                    onCancel={closeCustomize}
                    onClose={closeCustomize}
                    content={
                        <Box sx={{ pt: 1 }}>
                            <Typography
                                sx={{
                                    fontSize: "14px",
                                    fontWeight: 600,
                                    color: theme.palette.gray[700],
                                    mb: 1,
                                }}
                            >
                                Select hierarchies to include
                            </Typography>
                            <AcxHierarchySelector
                                orgId={
                                    authStore.orgStore.selectedOrganization
                                        ?.id ?? ""
                                }
                                userId={authStore._user.profile.sub}
                                displayType="input"
                                width="100%"
                                popperWidth="452px"
                                customZIndex={9999}
                                removeMinHeight
                                removeMinWidth
                                placeholder="Select....."
                                initSelectedHierarchyIds={selectedHierarchyIds}
                                setHierarchyIds={setSelectedHierarchyIds}
                                onSaveUpdateWithHierarchies={() => null}
                            />
                        </Box>
                    }
                />
            )}
        </Box>
    );
};

export default observer(HomeInsightsCards);
